import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, User, Phone, Mail, MapPin, Home, Building2, Calendar,
    Clock, FileText, Ruler, Zap, CheckCircle, Loader2, Sun
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';

const ExpertVisitModal = ({ isOpen, onClose, productId = null, productName = null }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [step, setStep] = useState(1); // 1: Contact, 2: Property, 3: Schedule

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        pincode: '',
        property_type: 'home',
        roof_area_sqft: '',
        monthly_bill: '',
        preferred_date: '',
        preferred_time: '',
        notes: ''
    });

    // Pre-fill form with logged-in user data
    useEffect(() => {
        if (user && isOpen) {
            setFormData(prev => ({
                ...prev,
                name: user.name || prev.name,
                email: user.email || prev.email,
                phone: user.phone || prev.phone
            }));
        }
    }, [user, isOpen]);

    const [errors, setErrors] = useState({});

    const validateStep = (currentStep) => {
        const newErrors = {};

        if (currentStep === 1) {
            if (!formData.name || formData.name.length < 2) {
                newErrors.name = 'Name is required (min 2 characters)';
            }
            if (!formData.phone || !/^\d{10}$/.test(formData.phone)) {
                newErrors.phone = 'Valid 10-digit phone number required';
            }
            if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                newErrors.email = 'Invalid email format';
            }
        }

        if (currentStep === 2) {
            if (!formData.address || formData.address.length < 10) {
                newErrors.address = 'Full address required (min 10 characters)';
            }
            if (!formData.city) {
                newErrors.city = 'City is required';
            }
            if (!formData.pincode || !/^\d{6}$/.test(formData.pincode)) {
                newErrors.pincode = 'Valid 6-digit pincode required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setStep(step + 1);
        }
    };

    const handleBack = () => {
        setStep(step - 1);
    };

    const handleSubmit = async () => {
        // Only submit on step 3
        if (step !== 3) {
            return;
        }

        if (!validateStep(3)) return;

        setLoading(true);

        try {
            const submitData = {
                name: formData.name,
                phone: formData.phone,
                email: formData.email || null,
                address: formData.address,
                city: formData.city,
                pincode: formData.pincode,
                property_type: formData.property_type,
                roof_area_sqft: formData.roof_area_sqft ? parseFloat(formData.roof_area_sqft) : null,
                monthly_bill: formData.monthly_bill ? parseFloat(formData.monthly_bill) : null,
                preferred_date: formData.preferred_date || null,
                preferred_time: formData.preferred_time || null,
                notes: formData.notes || null,
                product_id: productId || null
            };

            await axios.post(`${API}/api/expert-visits`, submitData);

            setSuccess(true);
            toast.success('Expert visit request submitted successfully!');

            // Reset after 3 seconds and close
            setTimeout(() => {
                setSuccess(false);
                setStep(1);
                setFormData({
                    name: user?.name || '',
                    phone: user?.phone || '',
                    email: user?.email || '',
                    address: '',
                    city: '',
                    pincode: '',
                    property_type: 'home',
                    roof_area_sqft: '',
                    monthly_bill: '',
                    preferred_date: '',
                    preferred_time: '',
                    notes: ''
                });
                onClose();
            }, 3000);

        } catch (error) {
            console.error('Error submitting request:', error);
            toast.error('Failed to submit request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    // Get tomorrow's date for min date
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = tomorrow.toISOString().split('T')[0];

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
                >
                    {/* Header - Solar Yellow/Orange Theme */}
                    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 p-6 text-white relative">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <Sun className="w-6 h-6" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">Request Expert Visit</h2>
                                <p className="text-amber-100 text-sm">
                                    {productName ? `For: ${productName}` : 'Get a free solar assessment'}
                                </p>
                            </div>
                        </div>

                        {/* Progress Steps */}
                        {!success && (
                            <div className="flex items-center justify-center gap-2 mt-4">
                                {[1, 2, 3].map((s) => (
                                    <div key={s} className="flex items-center">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${step >= s ? 'bg-white text-amber-600' : 'bg-white/30 text-white'
                                            }`}>
                                            {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                                        </div>
                                        {s < 3 && (
                                            <div className={`w-8 h-1 mx-1 rounded ${step > s ? 'bg-white' : 'bg-white/30'}`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Success State */}
                    {success ? (
                        <div className="p-8 text-center">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', duration: 0.5 }}
                                className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"
                            >
                                <CheckCircle className="w-10 h-10 text-amber-600" />
                            </motion.div>
                            <h3 className="text-xl font-bold text-foreground mb-2">Request Submitted!</h3>
                            <p className="text-muted-foreground">
                                Our team will contact you within 24 hours to confirm your expert visit.
                            </p>
                        </div>
                    ) : (
                        <div>
                            <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
                                {/* Step 1: Contact Details */}
                                {step === 1 && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="space-y-4"
                                    >
                                        <h3 className="font-semibold text-lg flex items-center gap-2">
                                            <User className="w-5 h-5 text-primary" />
                                            Contact Details
                                        </h3>

                                        {user && (
                                            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-sm">
                                                <p className="text-primary font-medium flex items-center gap-2">
                                                    <CheckCircle className="w-4 h-4" />
                                                    Pre-filled from your account
                                                </p>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label>Full Name *</Label>
                                            <Input
                                                placeholder="Enter your full name"
                                                value={formData.name}
                                                onChange={(e) => handleChange('name', e.target.value)}
                                                className={errors.name ? 'border-red-500' : ''}
                                            />
                                            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Phone Number *</Label>
                                            <div className="relative">
                                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    type="tel"
                                                    placeholder="10-digit mobile number"
                                                    value={formData.phone}
                                                    onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                                    className={`pl-10 ${errors.phone ? 'border-red-500' : ''}`}
                                                />
                                            </div>
                                            {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Email (Optional)</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    type="email"
                                                    placeholder="your@email.com"
                                                    value={formData.email}
                                                    onChange={(e) => handleChange('email', e.target.value)}
                                                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                                                />
                                            </div>
                                            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                                        </div>
                                    </motion.div>
                                )}

                                {/* Step 2: Property Details */}
                                {step === 2 && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="space-y-4"
                                    >
                                        <h3 className="font-semibold text-lg flex items-center gap-2">
                                            <MapPin className="w-5 h-5 text-primary" />
                                            Property Details
                                        </h3>

                                        <div className="space-y-2">
                                            <Label>Property Type *</Label>
                                            <Select value={formData.property_type} onValueChange={(v) => handleChange('property_type', v)}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="home">
                                                        <div className="flex items-center gap-2">
                                                            <Home className="w-4 h-4" /> Residential Home
                                                        </div>
                                                    </SelectItem>
                                                    <SelectItem value="commercial">
                                                        <div className="flex items-center gap-2">
                                                            <Building2 className="w-4 h-4" /> Commercial Building
                                                        </div>
                                                    </SelectItem>
                                                    <SelectItem value="industrial">
                                                        <div className="flex items-center gap-2">
                                                            <Building2 className="w-4 h-4" /> Industrial Facility
                                                        </div>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Full Address *</Label>
                                            <Textarea
                                                placeholder="House/Flat No., Street, Locality..."
                                                value={formData.address}
                                                onChange={(e) => handleChange('address', e.target.value)}
                                                className={errors.address ? 'border-red-500' : ''}
                                                rows={2}
                                            />
                                            {errors.address && <p className="text-red-500 text-sm">{errors.address}</p>}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>City *</Label>
                                                <Input
                                                    placeholder="City"
                                                    value={formData.city}
                                                    onChange={(e) => handleChange('city', e.target.value)}
                                                    className={errors.city ? 'border-red-500' : ''}
                                                />
                                                {errors.city && <p className="text-red-500 text-sm">{errors.city}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Pincode *</Label>
                                                <Input
                                                    placeholder="6-digit pincode"
                                                    value={formData.pincode}
                                                    onChange={(e) => handleChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                    className={errors.pincode ? 'border-red-500' : ''}
                                                />
                                                {errors.pincode && <p className="text-red-500 text-sm">{errors.pincode}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Roof Area (sq ft)</Label>
                                                <div className="relative">
                                                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                    <Input
                                                        type="number"
                                                        placeholder="e.g., 500"
                                                        value={formData.roof_area_sqft}
                                                        onChange={(e) => handleChange('roof_area_sqft', e.target.value)}
                                                        className="pl-10"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Monthly Bill (₹)</Label>
                                                <div className="relative">
                                                    <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                    <Input
                                                        type="number"
                                                        placeholder="e.g., 5000"
                                                        value={formData.monthly_bill}
                                                        onChange={(e) => handleChange('monthly_bill', e.target.value)}
                                                        className="pl-10"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                {/* Step 3: Schedule */}
                                {step === 3 && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="space-y-4"
                                    >
                                        <h3 className="font-semibold text-lg flex items-center gap-2">
                                            <Calendar className="w-5 h-5 text-primary" />
                                            Preferred Schedule (Optional)
                                        </h3>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Preferred Date</Label>
                                                <Input
                                                    type="date"
                                                    min={minDate}
                                                    value={formData.preferred_date}
                                                    onChange={(e) => handleChange('preferred_date', e.target.value)}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Preferred Time</Label>
                                                <Select value={formData.preferred_time} onValueChange={(v) => handleChange('preferred_time', v)}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select time" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="morning">
                                                            <div className="flex items-center gap-2">
                                                                <Clock className="w-4 h-4" /> Morning (9 AM - 12 PM)
                                                            </div>
                                                        </SelectItem>
                                                        <SelectItem value="afternoon">
                                                            <div className="flex items-center gap-2">
                                                                <Clock className="w-4 h-4" /> Afternoon (12 PM - 4 PM)
                                                            </div>
                                                        </SelectItem>
                                                        <SelectItem value="evening">
                                                            <div className="flex items-center gap-2">
                                                                <Clock className="w-4 h-4" /> Evening (4 PM - 7 PM)
                                                            </div>
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Additional Notes</Label>
                                            <Textarea
                                                placeholder="Any specific requirements or questions..."
                                                value={formData.notes}
                                                onChange={(e) => handleChange('notes', e.target.value)}
                                                rows={3}
                                                maxLength={500}
                                            />
                                            <p className="text-xs text-muted-foreground text-right">
                                                {formData.notes.length}/500
                                            </p>
                                        </div>

                                        {/* Summary */}
                                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 space-y-2 border border-amber-200 dark:border-amber-800">
                                            <h4 className="font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2">
                                                <Sun className="w-4 h-4" /> Summary
                                            </h4>
                                            <div className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                                                <p><strong>Name:</strong> {formData.name}</p>
                                                <p><strong>Phone:</strong> {formData.phone}</p>
                                                <p><strong>Address:</strong> {formData.address}, {formData.city} - {formData.pincode}</p>
                                                <p><strong>Property:</strong> {formData.property_type}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t bg-muted/30 flex items-center justify-between">
                                {step > 1 ? (
                                    <Button type="button" variant="outline" onClick={handleBack}>
                                        Back
                                    </Button>
                                ) : (
                                    <Button type="button" variant="ghost" onClick={onClose}>
                                        Cancel
                                    </Button>
                                )}

                                {step < 3 ? (
                                    <Button type="button" onClick={handleNext} className="btn-primary">
                                        Next
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Submit Request
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ExpertVisitModal;
